import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createLocation,
  deleteLocation,
  getLocations,
  updateLocation,
} from '../services/location.service';
import {
  GetLocationsParams,
  LocationInsertInput,
  LocationUpdateInput,
} from '../types/location.types';

const invalidateLocationQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-locations'] });

export const useGetLocations = (params: GetLocationsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-locations', params],
    queryFn: () => getLocations(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: LocationInsertInput) => createLocation(input),
    onSuccess: () => {
      invalidateLocationQueries(queryClient);
      toast({ title: t('LOCATION_CREATED') });
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: LocationUpdateInput }) => updateLocation(params),
    onSuccess: () => {
      invalidateLocationQueries(queryClient);
      toast({ title: t('LOCATION_UPDATED') });
    },
  });
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteLocation(itemId),
    onSuccess: () => {
      invalidateLocationQueries(queryClient);
      toast({ title: t('LOCATION_DELETED') });
    },
  });
};
